/**
 * Custom element for a single absorbency option.
 *
 * Attributes:
 * - label: string displayed next to the dot
 * - color: CSS color value for the dot
 * - increment: numeric increment size (defaults to 1)
 * - sku: identifier used in emitted events
 * - quantity: integer multiplier of increment (defaults to 0)
 * - disabled: disables the controls when present
 * - increment-disabled: disables the increment button when present
 * - decrement-disabled: disables the decrement button when present
 *
 * Events (bubble + composed):
 * - absorbency:increment { detail: { sku, increment, label } }
 * - absorbency:decrement { detail: { sku, increment, label } }
 */
class AbsorbencyOption extends HTMLElement {
  static get observedAttributes() {
    return ['label', 'color', 'increment', 'sku', 'quantity', 'disabled', 'increment-disabled', 'decrement-disabled', 'is-default', 'bundle-variant-id', 'pads-mixed-variant-id'];
  }

  constructor() {
    super();
    this._elements = {};
    this.handleIncrement = this.handleIncrement.bind(this);
    this.handleDecrement = this.handleDecrement.bind(this);
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (!this.isConnected) return;
    switch (name) {
      case 'label':
        this._elements.label && (this._elements.label.textContent = newValue || '');
        this.updateAria();
        break;
      case 'color':
        this._elements.dot && (this._elements.dot.style.backgroundColor = newValue || '#000');
        break;
      case 'increment':
      case 'quantity':
        this.updateQuantityDisplay();
        this.updateAria();
        break;
      case 'disabled':
      case 'increment-disabled':
      case 'decrement-disabled':
        this.updateDisabledState();
        break;
      default:
        break;
    }
  }

  get label() {
    return this.getAttribute('label') || '';
  }

  set label(value) {
    this.setAttribute('label', value);
  }

  get bundleVariantId() {
    return this.getAttribute('bundle-variant-id') || '';
  }

  set bundleVariantId(value) {
    this.setAttribute('bundle-variant-id', value);
  }

  get padsMixedVariantId() {
    return this.getAttribute('pads-mixed-variant-id') || '';
  }

  set padsMixedVariantId(value) {
    this.setAttribute('pads-mixed-variant-id', value);
  }

  get color() {
    return this.getAttribute('color') || '#000';
  }

  set color(value) {
    this.setAttribute('color', value);
  }

  get isDefault() {
    const val = this.getAttribute('is-default');
    if (val === null) return false;
    const normalized = val.toLowerCase().trim();
    return normalized === "1" || normalized === "true";
  }

  get increment() {
    const value = Number(this.getAttribute('increment'));
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  set increment(value) {
    this.setAttribute('increment', value);
  }

  get sku() {
    return this.getAttribute('sku') || '';
  }

  set sku(value) {
    this.setAttribute('sku', value);
  }

  get quantity() {
    const value = Number(this.getAttribute('quantity'));
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }

  set quantity(value) {
    this.setAttribute('quantity', value);
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(value) {
    if (value) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get incrementDisabled() {
    return this.hasAttribute('increment-disabled');
  }

  set incrementDisabled(value) {
    if (value) {
      this.setAttribute('increment-disabled', '');
    } else {
      this.removeAttribute('increment-disabled');
    }
  }

  get decrementDisabled() {
    return this.hasAttribute('decrement-disabled');
  }

  set decrementDisabled(value) {
    if (value) {
      this.setAttribute('decrement-disabled', '');
    } else {
      this.removeAttribute('decrement-disabled');
    }
  }

  handleIncrement() {
    if (this.disabled || this.incrementDisabled) return;
    this.dispatchEvent(
      new CustomEvent('absorbency:increment', {
        bubbles: true,
        composed: true,
        detail: { sku: this.sku, increment: this.increment, label: this.label, bundleVariantId: this.bundleVariantId, padsMixedVariantId: this.padsMixedVariantId }
      })
    );
  }

  handleDecrement() {
    if (this.disabled || this.decrementDisabled) return;
    this.dispatchEvent(
      new CustomEvent('absorbency:decrement', {
        bubbles: true,
        composed: true,
        detail: { sku: this.sku, increment: this.increment, label: this.label, bundleVariantId: this.bundleVariantId, padsMixedVariantId: this.padsMixedVariantId }
      })
    );
  }

  updateQuantityDisplay() {
    if (!this._elements.input) return;
    this._elements.input.textContent = this.quantity * this.increment;
  }

  updateDisabledState() {
    const disabled = this.disabled;
    if (this._elements.input) {
      this._elements.input.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }
    if (this._elements.incrementBtn) {
      this._elements.incrementBtn.disabled = disabled || this.incrementDisabled;
    }
    if (this._elements.decrementBtn) {
      this._elements.decrementBtn.disabled = disabled || this.decrementDisabled;
    }
  }

  updateAria() {
    if (!this._elements.input) return;
    this._elements.input.setAttribute(
      'aria-label',
      `Current quantity of ${this.label || 'this'} absorbency`
    );
    if (this._elements.incrementBtn) {
      this._elements.incrementBtn.setAttribute(
        'aria-label',
        `Increase ${this.label || 'this'} absorbency quantity`
      );
    }
    if (this._elements.decrementBtn) {
      this._elements.decrementBtn.setAttribute(
        'aria-label',
        `Decrease ${this.label || 'this'} absorbency quantity`
      );
    }
  }

  render() {
    this.innerHTML = `
      <div class="lola-absorbency-option-container">
        <label class="lola-absorbency-option-label">
          <span class="lola-absorbency-option-dot"></span>
          <span class="lola-absorbency-option-label-text"></span>
        </label>
        <div class="lola-absorbency-option-quantity">
          <div class="lola-absorbency-option-input" aria-readonly="true" role="textbox"></div>
          <div class="lola-absorbency-option-quantity-button-container">
            <button class="lola-quantity-button lola-quantity-button--up" type="button">▲</button>
            <button class="lola-quantity-button lola-quantity-button--down" type="button">▼</button>
          </div>
        </div>
      </div>
    `;

    this._elements = {
      dot: this.querySelector('.lola-absorbency-option-dot'),
      label: this.querySelector('.lola-absorbency-option-label-text'),
      input: this.querySelector('.lola-absorbency-option-input'),
      incrementBtn: this.querySelector('.lola-quantity-button--up'),
      decrementBtn: this.querySelector('.lola-quantity-button--down')
    };

    this._elements.dot.style.backgroundColor = this.color;
    this._elements.label.textContent = this.label;
    this.updateQuantityDisplay();
    this.updateDisabledState();
    this.updateAria();

    this._elements.incrementBtn.addEventListener('click', this.handleIncrement);
    this._elements.decrementBtn.addEventListener('click', this.handleDecrement);
  }
}

if (!customElements.get('absorbency-option')) {
  customElements.define('absorbency-option', AbsorbencyOption);
}
